import type { OrderResponse, OrderStatus } from '@/services/ordersApi'
import { Button } from '@/components/ui/button'
import {
  Ban,
  Clock3,
  Loader2,
  MessageCircle,
  MoreHorizontal,
  Pencil,
  Receipt,
  UtensilsCrossed,
} from 'lucide-react'
import { formatOrderMoney, isOrderEditable } from './ordersUx'
import { formatWait, getWaitMinutes, getWaitTone, type WaitThresholds } from './useTicketTimer'

interface OrderTicketCardProps {
  order: OrderResponse
  now: number
  thresholds: WaitThresholds
  primaryAction: { label: string; next: OrderStatus } | null
  isUpdating: boolean
  onOpen: (order: OrderResponse) => void
  onAdvance: (order: OrderResponse, next: OrderStatus) => void
  onEdit: (order: OrderResponse) => void
  onNotify: (order: OrderResponse) => void
  onCancel: (order: OrderResponse) => void
}

const TICKET_WAIT_CLASS: Record<ReturnType<typeof getWaitTone>, string> = {
  normal: 'bg-slate-100 text-slate-700',
  warn: 'bg-amber-100 text-amber-900',
  critical: 'bg-rose-100 text-rose-900',
}

function isPickup(order: OrderResponse): boolean {
  const method = (order.fulfillmentMethod || '').toLowerCase()
  return ['pickup', 'store_pickup', 'retiro'].includes(method)
}

export default function OrderTicketCard({
  order,
  now,
  thresholds,
  primaryAction,
  isUpdating,
  onOpen,
  onAdvance,
  onEdit,
  onNotify,
  onCancel,
}: OrderTicketCardProps) {
  const minutes = getWaitMinutes(order.createdAt, now)
  const tone = getWaitTone(minutes, thresholds)
  const items = order.items.slice(0, 4)
  const remaining = order.items.length - items.length

  return (
    <article
      className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm transition-shadow hover:shadow-md"
      aria-label={`Comanda ${order.orderNumber}`}
    >
      <div className="flex items-start justify-between gap-2">
        <button
          type="button"
          onClick={() => onOpen(order)}
          className="min-w-0 flex-1 rounded text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
          aria-label={`Abrir comanda ${order.orderNumber}`}
        >
          <p className="font-mono text-[11px] text-slate-500">#{order.orderNumber}</p>
          <p className="truncate text-sm font-bold text-slate-900">
            {order.customerName || order.customerPhone || 'Sin cliente'}
          </p>
        </button>
        <span
          className={[
            'inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold tabular-nums',
            TICKET_WAIT_CLASS[tone],
          ].join(' ')}
          aria-label={`Esperando ${formatWait(minutes)}${tone === 'critical' ? ', demora alta' : tone === 'warn' ? ', revisar' : ''}`}
        >
          <Clock3 className="h-3 w-3" />
          {formatWait(minutes)}
        </span>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        <span className={['rounded-full px-2 py-0.5 text-[11px] font-semibold', isPickup(order) ? 'bg-violet-100 text-violet-800' : 'bg-blue-100 text-blue-800'].join(' ')}>
          {isPickup(order) ? 'Retiro' : 'Delivery'}
        </span>
        <span className="text-[11px] text-slate-500">{formatOrderMoney(order.totalCents, order.currency)}</span>
      </div>

      <ul className="mt-2 space-y-1">
        {items.map((item) => (
          <li key={item.id} className="flex items-start gap-2 text-xs text-slate-800">
            <span className="font-black tabular-nums text-slate-900">{item.quantity}×</span>
            <span className="min-w-0 truncate">{item.name}</span>
          </li>
        ))}
        {remaining > 0 ? <li className="text-[11px] text-slate-500">+{remaining} más</li> : null}
      </ul>

      {order.notes ? (
        <p className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-2 py-1 text-[11px] font-medium text-amber-900">
          {order.notes}
        </p>
      ) : null}

      <div className="mt-3 flex items-center gap-1.5">
        {primaryAction ? (
          <Button
            type="button"
            size="sm"
            disabled={isUpdating}
            onClick={() => onAdvance(order, primaryAction.next)}
            className="min-h-[44px] flex-1 bg-brand text-white hover:bg-brand-dark"
          >
            {isUpdating ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <UtensilsCrossed className="mr-1.5 h-4 w-4" />}
            {primaryAction.label}
          </Button>
        ) : (
          <Button type="button" size="sm" variant="outline" onClick={() => onOpen(order)} className="min-h-[44px] flex-1">
            Ver detalle
          </Button>
        )}
        <details className="relative">
          <summary
            className="inline-flex h-11 w-11 cursor-pointer list-none items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 [&::-webkit-details-marker]:hidden"
            aria-label={`Más acciones para ${order.orderNumber}`}
          >
            <MoreHorizontal className="h-4 w-4" />
          </summary>
          <div className="absolute bottom-full right-0 z-20 mb-1 w-44 overflow-hidden rounded-xl border border-slate-200 bg-white py-1 shadow-lg">
            <button type="button" onClick={() => onOpen(order)} className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50">
              <Receipt className="h-4 w-4 text-slate-400" /> Ver detalle
            </button>
            {isOrderEditable(order.status) ? (
              <button type="button" onClick={() => onEdit(order)} className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50">
                <Pencil className="h-4 w-4 text-slate-400" /> Editar
              </button>
            ) : null}
            <button type="button" onClick={() => onNotify(order)} className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50">
              <MessageCircle className="h-4 w-4 text-slate-400" /> Notificar
            </button>
            {order.status !== 'completed' && order.status !== 'cancelled' ? (
              <button type="button" onClick={() => onCancel(order)} className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-rose-700 hover:bg-rose-50">
                <Ban className="h-4 w-4" /> Cancelar
              </button>
            ) : null}
          </div>
        </details>
      </div>
    </article>
  )
}
